import React, { useState } from 'react'
import { useBranding } from '@/contexts/BrandingContext'
import { getPageStyles, getCardStyles, getStatusColors } from '@/utils/pageBranding'
import { DocumentService, formatDate, formatFileSize } from '@/services/documentService'
import { Document, DocumentReview } from '@/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

interface AdminDocumentReviewProps {
  document: Document
  onReviewComplete: () => void
}

export const AdminDocumentReview: React.FC<AdminDocumentReviewProps> = ({
  document,
  onReviewComplete,
}) => {
  const { branding } = useBranding()
  const pageStyles = getPageStyles(branding)
  const cardStyles = getCardStyles(branding)
  const statusColors = getStatusColors(branding)
  const [review, setReview] = useState<DocumentReview>({
    status: 'APPROVED',
    review_notes: '',
    rejection_reason: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (review.status === 'REJECTED' && !review.rejection_reason) {
      setError('Rejection reason is required when rejecting a document')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      await DocumentService.updateDocument(document.id, review)
      onReviewComplete()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to review document')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="bg-gray-900  rounded-lg shadow p-6" style={pageStyles.container}>
      <h3 className="text-lg text-gray-400 font-semibold mb-4" style={pageStyles.text}>Review Document</h3>

      {/* Document Info */}
      <div className="mb-6 p-4 bg-gray-800 rounded-lg" style={cardStyles}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h4 className="font-medium text-gray-400" style={pageStyles.text}>Document Information</h4>
            <p className="text-sm text-gray-400" style={pageStyles.text}>Type: {document.document_type_display}</p>
            <p className="text-sm text-gray-400" style={pageStyles.text}>Priority: {document.priority_display}</p>
            <p className="text-sm text-gray-400" style={pageStyles.text}>Size: {formatFileSize(document.file_size)}</p>
            <p className="text-sm text-gray-400" style={pageStyles.text}>Uploaded: {formatDate(document.uploaded_at)}</p>
          </div>
          <div>
            <h4 className="font-medium text-gray-400" style={pageStyles.text}>User Information</h4>
            <p className="text-sm text-gray-400" style={pageStyles.text}>Name: {document.user_full_name}</p>
            <p className="text-sm text-gray-400" style={pageStyles.text}>Email: {document.user_email}</p>
          </div>
        </div>

        <div className="mt-4">
          <Button
            type="button"
            variant="outline"
            className="text-white bg-blue-500/20 border-blue-500/30 hover:bg-blue-500/30"
            style={{ backgroundColor: `${statusColors.info.backgroundColor}20`, borderColor: `${statusColors.info.borderColor}40`, color: statusColors.info.color }}
            size="sm"
            onClick={() => window.open(document.file, '_blank')}
          >
            View Document
          </Button>
        </div>
      </div>

      {/* Review Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-red-500/20 border border-red-400 rounded-lg p-3" style={{ backgroundColor: `${statusColors.error.backgroundColor}20`, borderColor: `${statusColors.error.borderColor}40`, color: statusColors.error.color }}>
            <p className="text-red-400 text-sm" style={{ color: statusColors.error.color }}>{error}</p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-400 mb-2" style={pageStyles.text}>Review Decision *</label>
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="radio"
                name="status"
                value="APPROVED"
                checked={review.status === 'APPROVED'}
                onChange={e => setReview({ ...review, status: e.target.value as any })}
                className="mr-2"
              />
              <span className="text-green-400 font-medium" style={{ color: statusColors.success.color }}>Approve</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="status"
                value="REJECTED"
                checked={review.status === 'REJECTED'}
                onChange={e => setReview({ ...review, status: e.target.value as any })}
                className="mr-2"
              />
              <span className="text-red-400 font-medium" style={{ color: statusColors.error.color }}>Reject</span>
            </label>
          </div>
        </div>

        {review.status === 'REJECTED' && (
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2" style={pageStyles.text}>
              Rejection Reason *
            </label>
            <Input
              placeholder="Please provide a reason for rejection..."
              value={review.rejection_reason}
              onChange={e => setReview({ ...review, rejection_reason: e.target.value })}
              required
            />
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-400 mb-2" style={pageStyles.text}>
            Review Notes (Optional)
          </label>
          <textarea
            className="w-full px-3 py-2 border border-gray-400 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            style={{ backgroundColor: `${branding.surfaceColor}80`, borderColor: `${branding.textSecondaryColor}30`, color: branding.textColor }}
            rows={3}
            placeholder="Add any additional notes about this document..."
            value={review.review_notes}
            onChange={e => setReview({ ...review, review_notes: e.target.value })}
          />
        </div>

        <div className="flex justify-end space-x-2">
          <Button type="button" variant="outline" onClick={() => onReviewComplete()} style={{ backgroundColor: `${branding.surfaceColor}80`, borderColor: `${branding.textSecondaryColor}30`, color: branding.textColor }}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            className={
              review.status === 'APPROVED'
                ? 'bg-green-400 hover:bg-green-700'
                : 'bg-red-400 hover:bg-red-700'
            }
            style={{ backgroundColor: review.status === 'APPROVED' ? `${statusColors.success.backgroundColor}` : `${statusColors.error.backgroundColor}`, borderColor: review.status === 'APPROVED' ? `${statusColors.success.borderColor}` : `${statusColors.error.borderColor}`, color: review.status === 'APPROVED' ? `${statusColors.success.color}` : `${statusColors.error.color}` }}
          >
            {isSubmitting
              ? 'Processing...'
              : `${review.status === 'APPROVED' ? 'Approve' : 'Reject'} Document`}
          </Button>
        </div>
      </form>
    </div>
  )
}
